import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

import { decideNavigation } from '@/auth/guard'
import { hasPermission, useSession } from '@/auth/session'
import AppLayout from '@/layouts/AppLayout.vue'

/**
 * Permissions are declared per route, in the same `project:object:action`
 * form the backend uses.
 *
 * Declaring them here does NOT protect anything: the guard below only
 * hides what the user cannot use. Every one of these routes calls an
 * endpoint that checks the same permission server-side, and that check is
 * the one that counts.
 */
declare module 'vue-router' {
  interface RouteMeta {
    /** Permission required to see this route in the navigation. */
    permission?: string
    /** Reachable without a session (login, access request, 404). */
    public?: boolean
    /** Key under `views` in the locale file, used for the page title. */
    titleKey?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: AppLayout,
    children: [
      {
        path: '',
        name: 'viewer',
        component: () => import('@/views/ViewerView.vue'),
        meta: { permission: 'maps:map:read', titleKey: 'viewer' },
      },
      {
        path: 'admin/properties',
        name: 'admin-properties',
        component: () => import('@/views/admin/PropertiesView.vue'),
        meta: { permission: 'maps:property:read', titleKey: 'properties' },
      },
      {
        path: 'admin/users',
        name: 'admin-users',
        component: () => import('@/views/admin/UsersView.vue'),
        meta: { permission: 'maps:user:read', titleKey: 'users' },
      },
      {
        path: 'admin/roles',
        name: 'admin-roles',
        component: () => import('@/views/admin/RolesView.vue'),
        meta: { permission: 'maps:role:read', titleKey: 'roles' },
      },
      {
        path: 'admin/plans',
        name: 'admin-plans',
        component: () => import('@/views/admin/PlansView.vue'),
        meta: { permission: 'maps:plan:read', titleKey: 'plans' },
      },
      {
        path: 'admin/plans/:id/review',
        name: 'admin-plan-review',
        component: () => import('@/views/admin/PlanReviewView.vue'),
        meta: { permission: 'maps:plan:read', titleKey: 'plans' },
      },
      {
        path: 'admin/audit',
        name: 'admin-audit',
        component: () => import('@/views/admin/AuditView.vue'),
        meta: { permission: 'maps:audit:read', titleKey: 'audit' },
      },
    ],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true },
  },
  {
    // Authenticated with Entra but not registered (or inactive) in the system.
    path: '/access-request',
    name: 'access-request',
    component: () => import('@/views/AccessRequestView.vue'),
    meta: { public: true },
  },
  {
    // The session is valid but the role lacks the permission of the route.
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    // Going back restores where the user was; a new screen starts at the top.
    return savedPosition ?? { top: 0 }
  },
})

const session = useSession()

router.beforeEach((to) => decideNavigation(to, { status: session.status.value, can: hasPermission }))
